import { useRef, useState } from "react";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { RESOURCE_LANDINGS } from "@/screens/resources/landings";
import ResourceSlot from "@/admin/ResourceSlot";
import { Switch } from "@/components/ui/switch";
import { useFetch, Panel, Loading, ErrorBox, apiError } from "@/admin/ui";

const EXTRA = {
  "practica-introspeccion": { pageTitle: "Práctica guiada de introspección (módulo de bienvenida)", path: "/practica-guiada" },
  "padres-video": { pageTitle: "Vídeo para padres y madres (página /padres)", path: "/padres" },
  "jovenes-video": { pageTitle: "Vídeo para jóvenes (home, bajo el módulo de bienvenida)", path: "/#video" },
};

const ResourcePanel = ({ res, files, onFileAdded }) => {
  const meta = RESOURCE_LANDINGS.find((r) => r.slug === res.slug) || EXTRA[res.slug];
  const [visible, setVisible] = useState(res.visible);
  const toggle = async (v) => {
    setVisible(v);
    try { await api.put(`/admin/resources/${res.slug}/visibility`, { visible: v }); toast.success(v ? "Ahora se ve en la web." : "Oculto en la web. Sus archivos se conservan."); }
    catch (e) { setVisible(!v); toast.error(apiError(e)); }
  };
  const [slots, setSlots] = useState(() => Object.fromEntries(res.slots.map((s) => [s.key, s.files.map((f) => f.id)])));
  const slotsRef = useRef(slots);
  const [saving, setSaving] = useState(false);
  const change = async (key, update) => {
    const prev = slotsRef.current;
    const next = { ...prev, [key]: typeof update === "function" ? update(prev[key] || []) : update };
    slotsRef.current = next;
    setSlots(next);
    setSaving(true);
    try { await api.put(`/admin/resources/${res.slug}`, { slots: next }); toast.success(visible ? "Guardado. Ya está visible en la web." : "Guardado. El recurso sigue oculto en la web."); }
    catch (e) { slotsRef.current = prev; setSlots(prev); toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Panel title={meta?.pageTitle || meta?.title || res.slug} testid={`admin-resource-${res.slug}`}
      action={<div className="adm-row" style={{ gap: ".6rem" }}>
        <label className="adm-row" style={{ gap: ".4rem", fontSize: 13 }}>
          <Switch checked={visible} onCheckedChange={toggle} data-testid={`admin-resource-visible-${res.slug}`} /> Visible en la web
        </label>
        <a href={meta?.path || `/recursos/${res.slug}`} target="_blank" rel="noreferrer" className="adm-btn sm" data-testid={`admin-resource-view-${res.slug}`}>Ver página <ExternalLink size={13} /></a>
        <span className="adm-sub" data-testid={`admin-resource-saving-${res.slug}`}>{saving ? "Guardando..." : "Se guarda automáticamente"}</span>
      </div>}>
      <div className="adm-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))" }}>
        {res.slots.map((s) => (
          <ResourceSlot key={s.key} slot={s} ids={slots[s.key]} files={files} onFileAdded={onFileAdded}
            onChange={(ids) => change(s.key, ids)} testid={`admin-slot-${res.slug}-${s.key}`} />
        ))}
      </div>
    </Panel>
  );
};

export default function AdminResources() {
  const { data, error } = useFetch("/admin/resources");
  const { data: files, setData: setFiles } = useFetch("/admin/files");
  if (error) return <ErrorBox />;
  if (!data || !files) return <Loading />;
  return (
    <div className="adm-grid" data-testid="admin-resources">
      <p className="adm-sub" style={{ margin: 0 }}>Los archivos de los recursos son gratuitos y se abren con una cuenta registrada, salvo la práctica de bienvenida y los vídeos para jóvenes y para padres, que son públicos. Cada cambio se guarda al momento.</p>
      {data.map((r) => <ResourcePanel key={r.slug} res={r} files={files} onFileAdded={(f) => setFiles([f, ...files])} />)}
    </div>
  );
}
